import {
  Box,
  CreateFilterOptionsConfig,
  PopperProps,
  Stack,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Control,
  FieldValues,
  RegisterOptions,
  useController,
} from 'react-hook-form';

import {
  getFieldErrorValue,
  getValidations,
} from '@/components/utils/inputUtils';
import { useSelectInputContext } from '@/modules/entities/contexts/selectInput/selectInput.context';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import {
  AddNewOptionDefinition,
  HelpTextVariant,
  SelectInputOption,
  TypeaheadSelectInputOption,
} from '../baseInputs/inputTypes';
import { TypeaheadSelectInput } from '../baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { useFormFieldsDisabled } from '../context/formFieldsDisabled.context';
import { useResponsiveSelectInput } from './hooks/useResponsiveSelectInput';

export interface FormAwareTypeaheadSelectInputProps<
  FormShape extends FieldValues,
  V = string,
> {
  fieldName: FieldNameFromFormShape<FormShape>;
  label: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  options: TypeaheadSelectInputOption<V>[];
  required?: boolean;
  control: Control<FormShape>;
  disabled?: boolean;
  validation?: RegisterOptions<FormShape>['validate'];
  contextualHelp?: JSX.Element;
  startAdornment?: React.ReactNode;
  testId?: string;
  // this `config` is used to generate filter options: https://mui.com/material-ui/react-autocomplete/#custom-filter
  // it's possible that we'll want to make this more flexible in the future and allow passing the factory function
  // at this level, but i think exposing the simpler API is reasonable for now
  config?: CreateFilterOptionsConfig<SelectInputOption<V>>;
  placeholder?: string;
  hideLabel?: boolean;
  dropdownProps?: Partial<PopperProps>;
  groupBy?: (option: TypeaheadSelectInputOption<V>) => string;
  addNewOption?: AddNewOptionDefinition;
  disableAutomaticNewOptionSelection?: boolean;
  /** @deprecated Use `placeholder`. */
  emptyOptionDisplay?: string;
  /** @deprecated Typeaheads no longer render a synthetic empty option. */
  hideEmptyOption?: boolean;
  disablePortal?: boolean;
  openOnFocus?: boolean;
  /** Icon rendered at the end of the field's label (e.g. AI suggestion locator). */
  labelIconEnd?: React.ReactNode;
  /** Icon rendered to the right of the input (used by the suggestion locator
   *  when positioned alongside the field rather than in the label). */
  rightLocatorIcon?: React.ReactNode;
  showErrorBeforeSubmit?: boolean;
}

export function FormAwareTypeaheadSelectInput<
  FormShape extends FieldValues,
  V = string,
>({
  fieldName,
  label,
  helpText,
  helpTextVariant,
  options,
  required,
  control,
  disabled,
  validation,
  contextualHelp,
  startAdornment,
  testId,
  config,
  placeholder,
  hideLabel,
  dropdownProps,
  groupBy,
  emptyOptionDisplay,
  hideEmptyOption,
  disablePortal,
  openOnFocus,
  addNewOption,
  disableAutomaticNewOptionSelection,
  labelIconEnd,
  rightLocatorIcon,
  showErrorBeforeSubmit,
}: FormAwareTypeaheadSelectInputProps<FormShape, V>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const {
    removeSelectFromStack,
    pushActiveSelectInput,
    peekActiveSelectInput,
    popActiveSelectInput,
  } = useSelectInputContext();
  const [isOpen, setIsOpen] = useState(false);
  const validations = getValidations(label, !!required, validation);
  const filterOptions = useMemo(() => createFilterOptions(config), [config]);
  const { field, fieldState, formState } = useController({
    name: fieldName,
    control,
    rules: { validate: validations },
  });

  useEffect(() => {
    return () => removeSelectFromStack(fieldName);
  }, [fieldName, removeSelectFromStack]);

  const handleClose = useCallback(() => {
    if (peekActiveSelectInput() === fieldName) {
      popActiveSelectInput();
    }

    setIsOpen(false);
  }, [fieldName, peekActiveSelectInput, popActiveSelectInput]);

  const handleOpen = useCallback(() => {
    pushActiveSelectInput(fieldName);
    setIsOpen(true);
  }, [fieldName, pushActiveSelectInput]);

  useResponsiveSelectInput<FormShape>({
    fieldName,
    fieldValue: field.value,
    selectValue: field.onChange,
    options,
    disableAutomaticNewOptionSelection,
    handleClose,
    isOpen,
  });

  const responsiveAddNewOption = useMemo(() => {
    if (!addNewOption) return undefined;

    return {
      ...addNewOption,
      onClick: () => {
        // Keep this input active while the creation modal is open so a newly
        // added option is selected even when creation takes several seconds.
        setIsOpen(false);
        addNewOption.onClick();
      },
    };
  }, [addNewOption]);

  const typeaheadInput = (
    <TypeaheadSelectInput<V>
      testId={testId}
      label={label}
      labelIconEnd={labelIconEnd}
      filterOptions={filterOptions}
      options={options}
      addNewOption={responsiveAddNewOption}
      onChange={(_e, value) => {
        // the underlying event doesn't have the appropriate value,
        // so we explicitly pass it through here.
        field.onChange({
          target: {
            value,
          },
        });
      }}
      hideLabel={hideLabel}
      groupBy={groupBy}
      onBlur={field.onBlur}
      value={field.value}
      name={field.name}
      inputRef={field.ref}
      required={required}
      helpText={helpText}
      dropdownProps={dropdownProps}
      helpTextVariant={helpTextVariant}
      disabled={disabledFromContext ?? disabled}
      contextualHelp={contextualHelp}
      errorMessage={getFieldErrorValue(
        fieldState,
        formState.isSubmitted,
        showErrorBeforeSubmit
      )}
      startAdornment={startAdornment}
      placeholder={placeholder}
      emptyOptionDisplay={emptyOptionDisplay}
      hideEmptyOption={hideEmptyOption}
      disablePortal={disablePortal}
      openOnFocus={openOnFocus}
      open={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
    />
  );

  if (!rightLocatorIcon) {
    return typeaheadInput;
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ flexGrow: 1 }}>{typeaheadInput}</Box>
      {rightLocatorIcon}
    </Stack>
  );
}
